import {
  Card,
  closeCurrentDialog,
  DataStatus,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

// eslint-disable-next-line no-restricted-imports -- Intended
import {isNil, mergeAll, path} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {metadaApi} from '@dms/api/base';
import {TenantResponseBody} from '@dms/api/shared';
import {
  GetReceiveNoteResponse,
  GetWarehouseResponse,
  usePostReceiveNoteItemsDeleteMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {DmsDataGrid, catchUnhandledDataGridActions, handleApiError, logError} from '@dms/shared';
import {OverviewTotalPrice} from '@dms/warehouse/shared-components';

import {composePath, Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {ItemRowData} from '../types/ItemRowData';
import {ReceiveNoteItemEdit, ReceiveNoteItemEditProps} from './ReceiveNoteItemEdit';
import {RemovalReason} from './RemovalReason';

interface ListOfItemsTabProps extends RequiredTestIdProps {
  receiveNoteId: string;
  receiveNote: GetReceiveNoteResponse | Nullish;
  warehouse: GetWarehouseResponse | Nullish;
  tenant: TenantResponseBody | Nullish;
  isDraftReceiveNote: boolean;
  isReceiveNotePending: boolean;
  isFromReceiveNote: boolean;
  totalPriceWithoutVat: string | null;
  totalPriceWithVat: string | null;
}

export function ListOfItemsTab(props: ListOfItemsTabProps) {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [editDialogProps, setEditDialogProps] = useState<ReceiveNoteItemEditProps | null>(null);

  const [deleteReceiveNoteItems] = usePostReceiveNoteItemsDeleteMutation();

  const actionCallback: ActionCallback = ({actionKey, rowData, queryId, refreshData, rowId}) => {
    const isBulk = isArray(rowData);
    const getName = (rowData: unknown) => path(['name', 'value'], rowData) as string;
    const receiveNoteItemId = path(['id'], rowData) as string;
    const warehouseId = props.receiveNote?.warehouseId;
    const articleId = path(['articleId', 'value'], rowData) as string;
    const name = isBulk
      ? (rowData.map((data: unknown) => getName(data)).join() as string)
      : (path(['name', 'value'], rowData) as string);
    const catalogNumber = path(['catalogNumber', 'value'], rowData) as string;
    const manufacturerId = path(['manufacturerId', 'value', 'id'], rowData) as string;

    if (!isBulk) {
      if (isNil(receiveNoteItemId)) {
        return logError('ReceiveNoteItemId is missing');
      }

      if (isNil(articleId)) {
        return logError('ArticleId is missing');
      }

      if (isNil(name)) {
        return logError('Name is missing');
      }

      if (isNil(catalogNumber)) {
        return logError('CatalogNumber is missing');
      }

      if (isNil(warehouseId)) {
        return logError('WarehouseId is missing');
      }

      if (isNil(manufacturerId)) {
        return logError('ManufacturerId is missing');
      }
    }

    const itemRowData: ItemRowData = {
      articleId,
      warehouseId: warehouseId as string,
      catalogNumber,
      name,
      manufacturerId,
      itemId: rowId as string,
    };

    match(actionKey)
      .with('redirectDetail', () => {
        navigate(
          composePath(warehouseRoutes.articleDetailOverview, {
            params: {
              warehouseId: props.receiveNote?.warehouseId,
              id: articleId,
            },
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('edit', () => {
        const handleClose = () => {
          setEditDialogProps(null);
          refreshData();
          // Invalidating manually onClose to keep dialog rowControl saving feature intact.
          dispatch(metadaApi.util.invalidateTags(['receiveNote', 'headerDynamicActions']));
        };

        setEditDialogProps({
          receiveNoteId: props.receiveNoteId,
          receiveNoteItemId,
          dataQueryId: queryId,
          authorizationProfileId: props.receiveNote?.authorizationProfileId,
          itemRowData,
          warehouse: props.warehouse,
          tenant: props.tenant,
          state: props.receiveNote?.state,
          isFromReceiveNote: props.isFromReceiveNote,
          receiveNoteType: props.receiveNote?.type,
          onClose: handleClose,
          'data-testid': suffixTestId('editItem', props),
        });
      })
      .with('delete', () => {
        handleDeleteReceiveNoteItem(rowId as string[], refreshData);
      })
      .with('deleteWithReason', () => {
        handleDeleteFromReceiptNote(rowId as string[], refreshData);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const handleRemove =
    (rowDataIds: string[], removeReason: string, refreshData: VoidFunction) => async () => {
      await deleteReceiveNoteItems({
        receiveNoteId: props.receiveNoteId,
        body: {
          receiveNoteItemId: rowDataIds,
          removeReason,
        },
      })
        .unwrap()
        .then(() => refreshData())
        .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
        .catch(handleApiError);
    };

  const handleDeleteReceiveNoteItem = (rowDataIds: string[], refreshData: VoidFunction) => {
    const EMPTY_REMOVAL_REASON = '';
    openDeleteDialog({
      onConfirm: handleRemove(rowDataIds, EMPTY_REMOVAL_REASON, refreshData),
    });
  };

  const handleDeleteFromReceiptNote = (rowDataIds: string[], refreshData: VoidFunction) => {
    const handleDiscard = () => {
      closeCurrentDialog();
    };

    const onRemove = async (receiveNoteItemId: string, removeReason: string) => {
      await handleRemove(rowDataIds, removeReason, refreshData)();
    };

    openDialog(
      <RemovalReason
        receiveNoteItemId={rowDataIds[0]}
        onRemove={onRemove}
        onDiscard={handleDiscard}
        data-testid={suffixTestId('forms.removalReason', props)}
      />,
      {
        id: 'REMOVAL_REASON_DIALOG',
        title: i18n.t('entity.warehouse.labels.removeFromReceiptNote'),
        size: 'small',
        'data-testid': suffixTestId('dialogs.removalReason', props),
      }
    );
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {receiveNoteId: props.receiveNoteId}]),
    [props.receiveNoteId]
  );

  return (
    <Card
      isFullHeight
      title={i18n.t('general.labels.listOfItems')}
      data-testid={suffixTestId(props['data-testid'], props)}
    >
      {isNil(editDialogProps) ? null : <ReceiveNoteItemEdit {...editDialogProps} />}
      <DataStatus isEmpty={props.isDraftReceiveNote} minHeight="90%">
        <Box height="95%">
          <DmsDataGrid
            gridCode="receive-note-item"
            actionCallback={actionCallback}
            queryModifier={queryModifier}
            data-testid={suffixTestId('datagrid.receiveNoteItems', props)}
          />
        </Box>
        <OverviewTotalPrice
          totalPriceWithoutVat={props.totalPriceWithoutVat}
          totalPriceWithVat={props.totalPriceWithVat}
          data-testid={suffixTestId('totalPrice', props)}
        />
      </DataStatus>
    </Card>
  );
}
